<template>
  <div class="col-12 pt-0">
    <div class="flex flex-column mb-3">
      <div class="has-roles flex align-items-center gap-2">
        <label class="form-item__label">Tiene roles</label>
        <InputSwitch v-model="hasRoles" :disabled="hasRolesDisabled" />
      </div>
      <div
        v-if="hasRolesDisabled"
        class="flex gap-2 align-items-center text-sm mt-1"
      >
        <i class="pi pi-info-circle"></i>
        <span>Si un informe es público no puede tener roles</span>
      </div>
    </div>

    <template v-if="hasRoles">
      <!-- Intorducir rol -->
      <InputGroup class="add-role-inputgroup">
        <InputText
          v-model="newRole"
          placeholder="Introducir rol"
          @keydown.enter="handleAddRol()"
        />
        <Button
          icon="pi pi-plus"
          @click="handleAddRol()"
          :disabled="!newRole"
        />
      </InputGroup>

      <DataTable :value="roles" class="roles-table mt-3">
        <template #empty>
          <div class="p-text-center">
            <span class="p-text-bold">No hay roles</span>
          </div>
        </template>
        <Column field="nombre" header="Rol">
          <template #body="slotProps">
            <InputText
              type="text"
              v-model="slotProps.data.nombre"
              placeholder="Nombre del rol"
              style="max-width: 15rem"
              @change="handleUpdateRol(slotProps.data)"
            />
          </template>
        </Column>
        <Column field="descripcion" header="Descripción">
          <template #body="slotProps">
            <InputText
              type="text"
              v-model="slotProps.data.descripcion"
              placeholder="Descripción del rol"
              style="max-width: 15rem"
              @change="handleUpdateRol(slotProps.data)"
            />
          </template>
        </Column>
        <Column field="orden" header="Orden">
          <template #body="slotProps">
            <InputText
              type="text"
              v-model="slotProps.data.orden"
              placeholder="Orden del rol"
              style="max-width: 5rem"
              @change="handleUpdateRol(slotProps.data)"
            />
          </template>
        </Column>
        <Column v-if="isEditing" header="Usuarios">
          <template #body="slotProps">
            <div class="flex flex-wrap gap-2">
              <div
                v-for="(rol, index) in slotProps.data.usuarios"
                :key="index"
                class="bg-primary text-white p-tag p-tag-rounded"
              >
                {{ rol.name }}
              </div>
            </div>
          </template>
        </Column>
        <Column>
          <template #body="slotProps">
            <div class="flex justify-content-end">
              <Button
                @click="handleDeleteRol(slotProps.data)"
                icon="pi pi-trash"
                severity="danger"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </template>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import Button from "primevue/button";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputGroup from "primevue/inputgroup";
import InputText from "primevue/inputtext";
import InputSwitch from "primevue/inputswitch";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";

import {
  userReportAddRole,
  userReportUpdateRole,
  userReportDeleteRole,
} from "@/api/reports/reports.api";
import { userReportDetails } from "@/api/reports/reports.api.js";

// #region Props, Emits
const props = defineProps({
  idInforme: {
    type: String,
    default: null,
  },
  alias: {
    type: String,
    default: null,
  },
  esPublico: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["changeHasRoles"]);
// #endregion

// #region Params, Ref, Reactives, Computed
const hasRoles = ref(false);
const hasRolesDisabled = ref(false);
const newRole = ref(null);
const roles = ref([]);
const isEditing = ref(false);
const confirm = useConfirm();
const toast = useToast();
// #endregion

// #region Functions
// Open the modal with the data
function loadForm(data = null) {
  if (data && Object.keys(data).length > 0 && data.rolesInforme) {
    roles.value = data.rolesInforme;
    hasRoles.value = data.tieneRoles;
    hasRolesDisabled.value = data.esPublico;
  }
}

async function handleAddRol() {
  if (newRole.value && newRole.value.trim() !== "") {
    const addRol = {
      idInforme: props.idInforme,
      nombre: newRole.value.trim(),
      descripcion: null,
      orden: roles.value.length + 1,
    };

    roles.value.push(addRol);

    if (props.idInforme == null || props.esPublico) {
      newRole.value = null;
    } else {
      const response = await userReportAddRole(addRol);
      const error = response.response?.data?.errors?.[0]?.message;

      if (!error) {
        reloadRoles(true);
        newRole.value = null;
      } else {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: error,
          life: 3000,
        });
      }
    }
  }
}

async function handleUpdateRol(rol) {
  if (props.idInforme == null || props.esPublico) return;

  roles.value.forEach((rolModel) => {
    const orden = parseInt(rolModel.orden);

    if (isNaN(orden)) {
      rolModel.orden = "";
    } else {
      rolModel.orden = orden;
    }
  });

  if (!checkSingleRole(rol)) {
    rol.idInforme = props.idInforme;
    await userReportUpdateRole(rol);
    reloadRoles();
  }
}

function handleDeleteRol(rol) {
  if (props.idInforme == null) {
    roles.value = roles.value.filter(
      (rolElement) => rolElement.nombre !== rol.nombre
    );
  } else {
    confirm.require({
      message: "¿Desea borrar el registro?",
      header: "Borrar",
      icon: "pi pi-exclamation-triangle",
      rejectClass: "p-button-secondary p-button-outlined",
      accept: async () => {
        const response = await userReportDeleteRole(props.idInforme, rol.orden);

        if (response.status != 200) {
          const error = response.response?.data?.errors?.[0]?.message;

          if (error) {
            toast.add({
              severity: "error",
              summary: "Error",
              detail: error,
              life: 3000,
            });
          } else {
            toast.add({
              severity: "error",
              summary: "Error",
              detail: "Error al borrar el rol",
              life: 3000,
            });
          }
        }

        reloadRoles();
      },
    });
  }
}

function checkRoles() {
  let invalid = false;

  roles.value.forEach((rol) => {
    invalid = checkSingleRole(rol);
  });

  return invalid;
}

function checkSingleRole(rol) {
  let invalid = false;

  if (!rol.nombre || !rol.descripcion || !rol.orden) {
    toast.add({
      severity: "error",
      summary: "Valor vacío",
      detail: "Debe introducir un nombre y descripción para cada rol",
      life: 3000,
    });

    invalid = true;
  }

  if (rol.orden < 1) {
    toast.add({
      severity: "error",
      summary: "Orden incorrecto",
      detail: 'El orden de un rol debe ser como mínimo "1"',
      life: 3000,
    });

    invalid = true;
  }

  if (rol.orden > roles.value.length) {
    toast.add({
      severity: "error",
      summary: "Orden incorrecto",
      detail: "El orden de un rol no puede ser mayor al número de roles",
      life: 3000,
    });

    invalid = true;
  }

  return invalid;
}

async function reloadRoles(fromCreating = false) {
  const response = await userReportDetails(props.alias);

  if (response.status != 200) {
    return;
  }

  const reportDetails = response.data;

  if (reportDetails.rolesInforme) {
    if (fromCreating) {
      const localLastRol = roles.value[roles.value.length - 1];
      const databaseLastRol =
        reportDetails.rolesInforme[reportDetails.rolesInforme.length - 1];

      localLastRol.idRolInforme = databaseLastRol.idRolInforme;
    } else {
      roles.value = reportDetails.rolesInforme;
    }
  }
}

function getData() {
  return roles.value;
}

function disableHasRolesField(disabled) {
  if (disabled) {
    hasRoles.value = false;
  }

  if (disabled) {
    toast.add({
      severity: "info",
      summary: "Cambio en los roles",
      detail: `Se ha desactivado la pestaña de roles`,
      life: 3000,
    });
  }

  hasRolesDisabled.value = disabled;
}
// #endregion

// #region Effects, Hooks
watch(
  () => hasRoles.value,
  (value) => {
    emit("changeHasRoles", value);
  },
  { deep: true }
);
// #endregion

// #region Provide, Expose
defineExpose({
  loadForm,
  checkRoles,
  getData,
  disableHasRolesField,
  hasRoles,
});
// #endregion
</script>
